import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Globe, Copy, Check, Share2, ArrowLeft, MessageSquarePlus, ExternalLink, Calendar, Sparkles } from "lucide-react";
import API from "../api/api";
import "../style/shared-response.css";

function SimpleMarkdown({ text }) {
  if (!text) return null;
  const lines = text.split("\n");
  const elements = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (/^####\s+/.test(line)) {
      elements.push(<h4 key={i} className="md-h4">{renderInline(line.replace(/^####\s+/, ""))}</h4>);
      i++;
      continue;
    }
    if (/^###\s+/.test(line)) {
      elements.push(<h3 key={i} className="md-h3">{renderInline(line.replace(/^###\s+/, ""))}</h3>);
      i++;
      continue;
    }
    if (/^##\s+/.test(line)) {
      elements.push(<h2 key={i} className="md-h2">{renderInline(line.replace(/^##\s+/, ""))}</h2>);
      i++;
      continue;
    }
    if (/^(\s*[-*•]|\s{2,}[◦▪])\s+/.test(line)) {
      const indent = line.match(/^(\s*)/)[1].length;
      const content = line.replace(/^\s*[-*•◦▪]\s+/, "");
      elements.push(
        <li key={i} className={`md-li ${indent > 0 ? "md-li-sub" : ""}`}>
          {renderInline(content)}
        </li>
      );
      i++;
      continue;
    }
    if (line.trim() === "") {
      elements.push(<div key={i} className="md-spacer" />);
      i++;
      continue;
    }
    elements.push(<p key={i} className="md-p">{renderInline(line)}</p>);
    i++;
  }
  return <div className="shared-markdown-content">{elements}</div>;
}

function renderInline(text) {
  const parts = text.split(/(\*\*.*?\*\*)/g);
  return parts.map((part, idx) =>
    /^\*\*.*\*\*$/.test(part) ? <strong key={idx}>{part.slice(2, -2)}</strong> : <span key={idx}>{part}</span>
  );
}

export default function SharedResponsePage() {
  const { shareId } = useParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sharedData, setSharedData] = useState(null);
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedText, setCopiedText] = useState(false);

  useEffect(() => {
    if (!shareId) return;

    let isMounted = true;
    setLoading(true);
    setError(null);

    API.get(`/chat/share/${shareId}`)
      .then((res) => {
        if (isMounted) {
          if (res.data?.success && res.data?.data) {
            setSharedData(res.data.data);
          } else {
            setError("Shared message not found.");
          }
        }
      })
      .catch((err) => {
        if (isMounted) {
          console.error("Failed to load shared message:", err);
          setError(
            err.response?.status === 404
              ? "This shared response does not exist or may have been deleted."
              : "Unable to load the shared response. Please try again."
          );
        }
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [shareId]);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2500);
    } catch (_) {}
  };

  const handleCopyResponse = async () => {
    if (!sharedData?.content) return;
    try {
      await navigator.clipboard.writeText(sharedData.content);
      setCopiedText(true);
      setTimeout(() => setCopiedText(false), 2500);
    } catch (_) {}
  };

  const handleShareNative = async () => {
    if (typeof navigator.share === "function") {
      try {
        await navigator.share({
          title: sharedData?.title || "GRGPT Response",
          text: sharedData?.question || "Check out this response on GRGPT:",
          url: window.location.href,
        });
      } catch (_) {}
    } else {
      handleCopyLink();
    }
  };

  const formattedDate = sharedData?.createdAt
    ? new Date(sharedData.createdAt).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : null;

  return (
    <div className="shared-page-container">
      {/* ── Top Header ── */}
      <header className="shared-page-header">
        <div className="shared-header-inner">
          <Link to="/" className="shared-brand-link" title="GRGPT Home">
            <img src="/grgpt-emblem.png" alt="GRGPT Emblem" className="shared-brand-logo" />
            <span className="shared-brand-name">GRGPT</span>
          </Link>

          <div className="shared-header-actions">
            <button
              type="button"
              onClick={handleCopyLink}
              className="shared-header-btn"
              title="Copy link"
            >
              {copiedLink ? <Check size={15} color="#10B981" /> : <Copy size={15} />}
              <span>{copiedLink ? "Copied Link" : "Copy Link"}</span>
            </button>

            <Link to="/" className="shared-cta-btn">
              <MessageSquarePlus size={16} />
              <span>Ask GRGPT</span>
            </Link>
          </div>
        </div>
      </header>

      {/* ── Main Content Area ── */}
      <main className="shared-main-wrap">
        {loading ? (
          <div className="shared-loading-box">
            <div className="shared-page-spinner" />
            <p>Loading shared response...</p>
          </div>
        ) : error ? (
          <div className="shared-error-card">
            <div className="shared-error-icon">⚠️</div>
            <h2>Shared Link Not Found</h2>
            <p>{error}</p>
            <Link to="/" className="shared-home-btn">
              <ArrowLeft size={16} />
              <span>Back to GRGPT</span>
            </Link>
          </div>
        ) : (
          <article className="shared-content-card">
            {/* Metadata Bar */}
            <div className="shared-meta-bar">
              <div className="shared-meta-badge">
                <Globe size={14} className="shared-meta-globe" />
                <span>Public Shared Response</span>
              </div>
              {formattedDate && (
                <div className="shared-meta-date">
                  <Calendar size={13} />
                  <span>{formattedDate}</span>
                </div>
              )}
            </div>

            {/* User Question */}
            {sharedData?.question && (
              <div className="shared-question-section">
                <div className="shared-section-header">
                  <span className="shared-author-label user">Prompt</span>
                </div>
                <div className="shared-question-bubble">
                  <p>{sharedData.question}</p>
                </div>
              </div>
            )}

            {/* Assistant Response */}
            <div className="shared-response-section">
              <div className="shared-section-header">
                <div className="shared-assistant-badge">
                  <img src="/grgpt-emblem.png" alt="GRGPT" className="shared-avatar-img" />
                  <span className="shared-author-label assistant">GRGPT Answer</span>
                </div>
              </div>

              <div className="shared-response-body">
                <SimpleMarkdown text={sharedData?.content} />
              </div>

              {/* Sources / Citations */}
              {Array.isArray(sharedData?.sources) && sharedData.sources.length > 0 && (
                <div className="shared-sources-box">
                  <h4 className="shared-sources-title">
                    <Sparkles size={14} /> Referenced Sources ({sharedData.sources.length})
                  </h4>
                  <div className="shared-sources-grid">
                    {sharedData.sources.map((src, idx) => {
                      const title = src.title || src.file_name || src.document_name || (typeof src === "string" ? src : `Source ${idx + 1}`);
                      const link = src.url || src.link || null;
                      return link ? (
                        <a
                          key={idx}
                          href={link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="shared-source-chip link"
                        >
                          <span className="shared-source-num">{idx + 1}</span>
                          <span className="shared-source-name">{title}</span>
                          <ExternalLink size={12} />
                        </a>
                      ) : (
                        <div key={idx} className="shared-source-chip">
                          <span className="shared-source-num">{idx + 1}</span>
                          <span className="shared-source-name">{title}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Action Toolbar on the shared response */}
              <div className="shared-actions-toolbar">
                <button
                  type="button"
                  onClick={handleCopyResponse}
                  className="shared-action-btn"
                  title="Copy full text"
                >
                  {copiedText ? <Check size={14} color="#10B981" /> : <Copy size={14} />}
                  <span>{copiedText ? "Copied!" : "Copy Response"}</span>
                </button>

                <button
                  type="button"
                  onClick={handleShareNative}
                  className="shared-action-btn"
                  title="Share link"
                >
                  <Share2 size={14} />
                  <span>Share</span>
                </button>

                <Link
                  to="/"
                  className="shared-action-btn cta"
                  title="Start your own conversation"
                >
                  <MessageSquarePlus size={14} />
                  <span>Ask a follow-up on GRGPT</span>
                </Link>
              </div>
            </div>
          </article>
        )}

        <footer className="shared-page-footer">
          <p>GRGPT is an AI assistant for government resolutions and public schemes.</p>
          <p className="disclaimer">Grgpt is AI and can make mistakes.</p>
        </footer>
      </main>
    </div>
  );
}
